import _ from "lodash";
import { getMemoizedFunction } from "Utils/GetMemoizedFunction";
import { getMemoizedLangFunction } from "Utils/GetMemoizedLangFunction";
import { getSettingValue } from "Utils/ConfigurationUtils";

export const ADD_ITEM_TO_TOP_PREFERENCE = "AddNewItemsToTopInFlows";
export const DISPLAY_SETTINGS_CONFIG_NAME = "DisplaySettingsInFlows";

export const FLOW = "flow";
export const UNIT = "unit";
export const CARD = "card";
export const TABLE = "table";
export const TIMELINE = "timeline";
export const SCHEDULE = "schedule";
export const CARD_VIEW = "cardView";
export const TIMELINE_VIEW = "timelineView";
export const COURSE_OVERVIEW = "courseOverview";
export const COURSE_PLAN = "course-plan";
export const COURSE_FLOW = "course-flow";

export const FLOW_DISPLAY_SETTINGS = {
  show_outline: "show_outline",
  collapse_all_sections: "collapse_all_sections",
  expand_all_sections: "expand_all_sections",
  thumbnail_image: "thumbnail_image",
  task_resource_type: "task_resource_type",
  task_status: "task_status",
  add_to_top: "add_to_top",
  add_to_bottom: "add_to_bottom",
  assessment_category: "assessment_category",
  due_or_scheduled_date: "due_or_scheduled_date",
  locked_status: "locked_status",
  show_blueprint_lock: "show_blueprint_lock",
  grading_period: "grading_period",
};

export const UNIT_DISPLAY_SETTINGS = {
  sort: "sort",
  title: "title",
  updated_at: "updated_at",
  CUSTOM: "custom",
  display: "display",
  start_date: "start_date",
  subjects: "subjects",
  unit_hours: "unit_hours",
  duration_month: "duration_month",
  level: "level",
  duration_week: "duration_week",
  learning_experiences: "learning_experiences",
  assessments: "assessments",
  collaborators: "collaborators",
  shared_with_students_family: "shared_with_students_family",
  show_blueprint_lock: "show_blueprint_lock",
  owner: "owner",
  unit_code: "unit_code",
};

// NOTE and LINK are explicitly shown in the filter bar
// these are the rest of them
const REST_RESOURCE_TYPES = [
  "VIDEO",
  "IMAGE",
  "FILE",
  "AUDIO",
  "WORKBOOK",
  "VOICE_NOTE",
  "QUESTION",
  "QUESTION_SUBMISSION",
];

const getResourceTypeOptions = ({ t }) => {
  return [
    { key: "NOTE", value: "NOTE", label: t("common:note_plural") },
    { key: "LINK", value: "LINK", label: t("common:links") },
    {
      key: "REST_RESOURCES",
      value: "REST_RESOURCES",
      label: t("common:resources"),
    },
  ];
};

export const getResourceTypeOptionsMemoized = getMemoizedLangFunction(
  getResourceTypeOptions
);

const splitAssessmentAndResourceTypes = ({ selectedItemTypes }) => {
  const validResourceTypes = _.map(getResourceTypeOptionsMemoized(), "value");
  const selectedAssessmentTypes = _.difference(
    selectedItemTypes,
    validResourceTypes
  );

  const selectedResourceTypes = _.reduce(
    selectedItemTypes,
    (result, type) => {
      if (_.includes(validResourceTypes, type)) {
        if (type === "REST_RESOURCES") {
          return [...result, ...REST_RESOURCE_TYPES];
        }

        return [...result, type];
      }

      return result;
    },
    []
  );

  return {
    selectedAssessmentTypes,
    selectedResourceTypes,
  };
};

export const splitAssessmentAndResourceTypesMemoized = getMemoizedFunction(
  splitAssessmentAndResourceTypes
);

const getItemTypeOptions = ({
  assessmentTypeOptions,
  availableCategories = [],
}) => {
  const resourceTypeOptions = getResourceTypeOptionsMemoized();
  if (_.isEmpty(availableCategories)) {
    return [...assessmentTypeOptions, ...resourceTypeOptions];
  } else {
    return _.reduce(
      availableCategories,
      (result, category) => {
        switch (category) {
          case "ASSESSMENT":
            return [...result, ...assessmentTypeOptions];
          case "ENTITY_FLOW_RESOURCE":
            return [...result, ...resourceTypeOptions];
          default:
            return result;
        }
      },
      []
    );
  }
};

export const getItemTypeOptionsMemoized = getMemoizedFunction(
  getItemTypeOptions
);

// memoized because it was causing recomputation of hooks
export const getFeedResources = getMemoizedFunction(feed => {
  return _.map(_.get(feed, "edges", []), "node");
});

export const getItemTypeLocale = ({ item, assessmentTypes }) => {
  const resourceType = _.get(item, "resourceType", "FILE");
  let itemLabelLocale = "common:resource_label";

  switch (resourceType) {
    case "UNIT_PLAN": {
      itemLabelLocale = "common:unit_plan";
      break;
    }
    case "FOLDER": {
      itemLabelLocale = "common:folder";
      break;
    }
    case "ASSESSMENT": {
      const assessmentType = _.get(item, "item.assessmentType.value", "");
      itemLabelLocale =
        assessmentTypes[assessmentType]?.localeLabel || "common:assessment";
      break;
    }
    default: {
      const attachmentType = _.get(item, "item.attachmentType", "FILE");

      if (attachmentType === "NOTE") {
        itemLabelLocale = "common:note";
      } else if (attachmentType === "LINK") {
        itemLabelLocale = "common:link";
      }
    }
  }

  return itemLabelLocale;
};

export const getAddNewItemToTopPreference = () => {
  const shouldAddNewItemsToTop = getSettingValue({
    name: ADD_ITEM_TO_TOP_PREFERENCE,
  });

  return shouldAddNewItemsToTop;
};

export const getDisplaySettingsConfig = () => {
  const displaySettingsConfig = getSettingValue({
    name: DISPLAY_SETTINGS_CONFIG_NAME,
  });

  return displaySettingsConfig;
};

// Cycles the nonce through 0→1→2→0 on every collapse/expand click so the
// flow's useEffect can detect re-clicks even when the boolean value is unchanged.
export const bumpCollapseExpandNonce = settings =>
  ((settings["collapse_expand_action_nonce"] || 0) + 1) % 3;
